import type { ComponentOrReferenceFormattingRule } from '@ardoq/data-model';
import { Select } from '@ardoq/select';
import { CommonDefaultFilterKeys } from '@ardoq/api-types';
import {
  GroupBox,
  InputsWrapper,
  RowHeader,
  RuleWarningContainer,
} from '../../../atoms';
import {
  useFormattingContext,
  perspectivesRowDragAndDropHandlers,
  WarningNotificationRule,
  SelectComparator,
  getValueForTypeField,
} from '@ardoq/perspectives';
import {
  getSelectFieldOptions,
  RuleNotificationType,
  QueryBuilderFilterType,
} from '@ardoq/query-builder';
import { useDragAndDropContext } from '@ardoq/drag-and-drop';
import { WarningNotification } from '@ardoq/status-ui';
import { FormattingRuleHeader } from './FormattingRuleHeader';
import { FormattingRulePopoverContent } from './FormattingRulePopoverContent';
import { FormattingRuleRowByComponentOrReferenceBody } from './FormattingRuleRowByComponentOrReferenceBody';
import { ListFieldFormattingRuleBody } from './ListFieldFormattingRuleBody';
import { SidebarFormattingRuleTypePicker } from './FormattingRuleTypePicker';
import { ColorRangeValue } from '@ardoq/perspectives';
import { FilterInfoOperator, FormattingRuleOperator } from '@ardoq/api-types';
import { isRuleVersionSupported } from '@ardoq/perspectives';
import { type I18nLocale, t } from '@ardoq/i18n';
import { isNil } from 'lodash';
import { canSetTypeField } from '@ardoq/perspectives';

type FormattingRuleByComponentOrReferenceProperties = {
  formattingRule: ComponentOrReferenceFormattingRule;
  colorPaletteAlignment: 'left' | 'right';
  i18nLocale?: I18nLocale;
};
export const FormattingRuleRowByComponentOrReference = ({
  formattingRule: rule,
  colorPaletteAlignment,
  i18nLocale = {},
}: FormattingRuleByComponentOrReferenceProperties) => {
  const formattingContext = useFormattingContext();
  const { updateRule, removeRule, getRuleNotificationParams, ruleInfo } =
    formattingContext;
  const { __id: ruleId, field, comparator } = rule;
  const { onDragStart, onDragOver, onDragEnd } =
    perspectivesRowDragAndDropHandlers(ruleId, useDragAndDropContext());

  const info = ruleInfo[ruleId];
  if (!info) {
    return null;
  }
  const { filterGroups, fieldFormatting, comparators } = info;
  const filter = field ? fieldFormatting?.[field] : null;
  const isInitialized = field !== null && field !== '';
  const ruleNotificationParams = isInitialized
    ? getRuleNotificationParams?.(rule)
    : null;
  const warningMessage =
    ruleNotificationParams?.notificationType === RuleNotificationType.WARNING
      ? ruleNotificationParams.message
      : null;

  if (isInitialized && !filter) {
    if (warningMessage) {
      return (
        <WarningNotificationRule
          message={warningMessage}
          removeRule={() => removeRule(ruleId)}
          onDragEnd={onDragEnd}
          onDragOver={onDragOver}
          dataTestId="group-box-formatting-warning-notification"
        />
      );
    }

    return (
      <GroupBox>
        {t(
          i18nLocale,
          'perspectives:formattingRuleRow.errorCouldNotLoad',
          { field: rule.field },
          'Error - could not load formatting rule "{{field}}"'
        )}
      </GroupBox>
    );
  }

  const maybeFieldLabel = field ? filter?.label : undefined;

  const popoverContent =
    rule.isCollapsed && field
      ? () => (
          <FormattingRulePopoverContent
            rule={rule}
            filterLabel={filter?.label}
            filterType={filter?.filterType}
          />
        )
      : undefined;

  /** Does this formatting rule represent a list field? */
  const isListFieldFormatting =
    filter?.filterType === QueryBuilderFilterType.SELECT &&
    comparator === FilterInfoOperator.EQUALS &&
    !isNil(rule.value);
  const fieldSelectPlaceholder = t(
    i18nLocale,
    'perspectivesSidebar:formattingRules.searchAndSelectField',
    'Search and select a field'
  );

  const isRuleUnsupported = !isRuleVersionSupported(true, rule.version);

  return (
    <GroupBox
      role="group"
      aria-label={t(
        i18nLocale,
        'perspectivesSidebar:formattingRules.conditionalFormattingRuleAriaLabel',
        'Conditional formatting rule'
      )}
      data-testid={`group-box-formatting-rule-by-${rule.type}`}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDragEnd={onDragEnd}
      flexDirection="column"
    >
      <FormattingRuleHeader
        context={formattingContext}
        rule={rule}
        label={maybeFieldLabel}
        popoverContent={popoverContent}
        i18nLocale={i18nLocale}
      />
      {warningMessage && (
        <RuleWarningContainer data-testid="group-box-formatting-warning-notification">
          <WarningNotification>{warningMessage}</WarningNotification>
        </RuleWarningContainer>
      )}
      {rule?.isCollapsed ? null : (
        <InputsWrapper $isDisabled={isRuleUnsupported}>
          <SidebarFormattingRuleTypePicker
            rule={rule}
            formattingContext={formattingContext}
          />
          <RowHeader>
            {t(
              i18nLocale,
              'perspectivesSidebar:formattingRules.field',
              'Field'
            )}
          </RowHeader>
          <Select
            aria-label={t(
              i18nLocale,
              'perspectivesSidebar:formattingRules.attributeAriaLabel',
              'Attribute'
            )}
            placeholder={fieldSelectPlaceholder}
            dataTestId="formatting-rule-field-select"
            value={field}
            options={getSelectFieldOptions(filterGroups ?? [])}
            onValueChange={field => {
              if (typeof field !== 'string') return; // shouldn't happen
              const filter = fieldFormatting?.[field];
              if (!filter) return; // shouldn't happen

              const value =
                field === CommonDefaultFilterKeys.TYPE
                  ? getValueForTypeField(rule.typeNames)
                  : null;
              if (!canSetTypeField(field, rule.typeNames)) return;
              updateRule(ruleId, {
                field,
                comparator: filter.comparators[0],
                value,
              });
            }}
            menuPortalTarget={null}
          />
          {filter &&
          field &&
          rule.comparator === FormattingRuleOperator.COLOR_RANGE ? (
            <>
              <RowHeader>
                {t(
                  i18nLocale,
                  'perspectivesSidebar:formattingRules.condition',
                  'Condition'
                )}
              </RowHeader>
              <SelectComparator
                selectedComparator={rule.comparator}
                comparators={comparators}
                onValueChange={comparator => {
                  if (!comparator) return; // shouldn't happen
                  // Reset value when changing comparator to avoid incompatible value types.
                  updateRule(rule.__id, { comparator, value: null });
                }}
                isNewConditionalFormatting={true}
                menuPortalTarget={null}
                filterType={filter?.filterType}
              />
              <ColorRangeValue
                rule={rule}
                formattingContext={formattingContext}
                isSidebar
                colorPaletteAlignment={colorPaletteAlignment}
                menuPortalTarget={null}
              />
            </>
          ) : filter && field && isListFieldFormatting ? (
            <ListFieldFormattingRuleBody
              rule={rule}
              filter={filter}
              formattingContext={formattingContext}
              colorPaletteAlignment={colorPaletteAlignment}
            />
          ) : filter && field ? (
            <FormattingRuleRowByComponentOrReferenceBody
              rule={rule}
              filter={filter}
              formattingContext={formattingContext}
              comparators={comparators}
              colorPaletteAlignment={colorPaletteAlignment}
            />
          ) : null}
        </InputsWrapper>
      )}
    </GroupBox>
  );
};
